import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { showToast } from '../../Components/utils/ShowToast';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import Loader from '../../Components/utils/Loader';
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';
import ImageCropModal from '../../Components/utils/ImageCropModel';

const API = process.env.REACT_APP_Backend_Host_Name;

function AdminBanners() {
    const [banners, setBanners] = useState([]);
    const [image, setImage] = useState(null);
    const [link, setLink] = useState('');
    const [editId, setEditId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            setIsLoading(true);
            const { data } = await axios.get(`${API}/api/v1/banners/admin/list`, { headers });
            if (data.success) setBanners(data.banners || []);
        } catch {
            showToast('Failed to load banners', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setImage(null);
        setLink('');
        setEditId(null);
        setShowForm(false);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageToCrop({ dataUrl: reader.result, filename: file.name });
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = (croppedFile) => {
        setImage(croppedFile);
        setCropModalOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!image && !editId) {
            showToast('Banner image is required', 'warning');
            return;
        }
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('link', link);
            if (image) formData.append('image', image);

            if (editId) {
                const { data } = await axios.put(`${API}/api/v1/banners/admin/${editId}`, formData, {
                    headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                });
                if (data.success) {
                    showToast('Banner updated', 'success');
                    resetForm();
                    fetchBanners();
                }
            } else {
                const { data } = await axios.post(`${API}/api/v1/banners/admin`, formData, {
                    headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                });
                if (data.success) {
                    showToast('Banner added', 'success');
                    resetForm();
                    fetchBanners();
                }
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save banner', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (banner) => {
        setEditId(banner._id);
        setLink(banner.link || '');
        setImage(null);
        setShowForm(true);
    };

    const handleDelete = async () => {
        if (!deleteConfirm.id) return;
        setIsLoading(true);
        try {
            const { data } = await axios.delete(`${API}/api/v1/banners/admin/${deleteConfirm.id}`, { headers });
            if (data.success) {
                showToast('Banner deleted', 'success');
                setDeleteConfirm({ open: false, id: null });
                fetchBanners();
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="adminBannersDiv">
            {isLoading && <Loader type="checkmate" />}
            <h1 className="primaryHeading">Marketing Banners</h1>
            <p className="adminBannersSubtext">Add banners displayed at the bottom of the Home page. Users can click to open the link in a new tab.</p>
            <div className="adminBannersDimensionHint">
                <span className="dimensionBadge">Recommended: 1200 × 400 px</span>
                <span className="dimensionNote">Aspect ratio 3:1 for best display</span>
            </div>

            <div className="adminBannersContent">
                <div className="bannersFormSection">
                    {!showForm ? (
                        <button type="button" className="primaryBtn addBannerBtn" onClick={() => setShowForm(true)} disabled={isLoading}>
                            <FaPlus /> Add Banner
                        </button>
                    ) : (
                        <form className="bannerForm bannerFormModern" onSubmit={handleSubmit}>
                            <h3 className="bannerFormTitle">{editId ? 'Edit Banner' : 'Add New Banner'}</h3>
                            <div className="bannerFormGrid">
                                <div className="bannerFormImageBlock">
                                    <label className="bannerFormLabel">Banner Image <span>*</span></label>
                                    <label className="bannerFormImageUpload">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="bannerFormFileInput"
                                            required={!editId}
                                            disabled={isLoading}
                                        />
                                        {(image || (editId && banners.find(b => b._id === editId)?.image)) ? (
                                            <div className="bannerFormPreview">
                                                <img src={image ? URL.createObjectURL(image) : banners.find(b => b._id === editId)?.image} alt="Preview" />
                                            </div>
                                        ) : (
                                            <div className="bannerFormPlaceholder">
                                                <FaPlus className="bannerFormPlaceholderIcon" />
                                                <span>Click to upload image</span>
                                                <span className="bannerFormPlaceholderHint">1200 × 400 px recommended</span>
                                            </div>
                                        )}
                                    </label>
                                    {editId && <p className="inputHint">Leave empty to keep current image.</p>}
                                </div>
                                <div className="bannerFormFieldsBlock">
                                    <div className="inputDiv">
                                        <label>Link URL</label>
                                        <input
                                            type="url"
                                            value={link}
                                            onChange={(e) => setLink(e.target.value)}
                                            placeholder="https://example.com"
                                            className="inputField"
                                            disabled={isLoading}
                                        />
                                        <p className="inputHint">URL opened when user clicks the banner. Leave empty for no link.</p>
                                    </div>
                                    <div className="buttonsRow" style={{ display: 'flex', gap: '10px' }}>
                                        <button type="submit" className="primaryBtn" disabled={isLoading}>
                                            {editId ? 'Update' : 'Add'} Banner
                                        </button>
                                        <button type="button" className="secondaryBtn" onClick={resetForm} disabled={isLoading}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                <div className="bannersListSection">
                    <h3 className="secondaryHeading">Current Banners</h3>
                    {banners.length === 0 ? (
                        <p className="noDataText">No banners yet. Add one to display on the Home page.</p>
                    ) : (
                        <div className="bannersList">
                            {banners.map(banner => (
                                <div key={banner._id} className="bannerCard">
                                    <div className="bannerCardImage">
                                        <img src={banner.image} alt="Banner" />
                                    </div>
                                    <div className="bannerCardMeta">
                                        {banner.link && <span className="bannerLink">{banner.link}</span>}
                                        {!banner.link && <span className="bannerNoLink">No link</span>}
                                    </div>
                                    <div className="bannerCardActions">
                                        <button type="button" className="iconBtn editBtn" onClick={() => handleEdit(banner)} title="Edit"><FaEdit /></button>
                                        <button type="button" className="iconBtn deleteBtn" onClick={() => setDeleteConfirm({ open: true, id: banner._id })} title="Delete"><FaTrash /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {cropModalOpen && imageToCrop && (
                <ImageCropModal
                    imageSrc={imageToCrop.dataUrl}
                    open={cropModalOpen}
                    onCancel={() => setCropModalOpen(false)}
                    onComplete={handleCropComplete}
                    aspect={3}
                    filename={imageToCrop.filename}
                />
            )}

            <ConfirmDialog
                open={deleteConfirm.open}
                title="Delete Banner"
                message="Are you sure you want to delete this banner?"
                onConfirm={handleDelete}
                onCancel={() => setDeleteConfirm({ open: false, id: null })}
                isLoading={isLoading}
            />
        </div>
    );
}

export default AdminBanners;
